import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Printer,
  ClipboardCopy,
  RotateCcw,
  Download,
  Info,
  AlertTriangle,
  Plus,
  Trash2,
  ExternalLink,
  ClipboardList,
  Receipt,
  Car,
  FileText,
  FilePlus2,
  CheckCircle2,
  HelpCircle,
  BookOpen,
  Save,
} from "lucide-react";

/* ══════════════════════════════════════════════════════════════════════
 * Tax Confidence Kit — Pro Carer Circle tool
 *
 * Purpose: help a self-employed carer ORGANISE income, expenses, mileage,
 * invoices, receipts and accountant questions into a printable pack. This
 * is NOT a tax calculator, NOT tax advice, NOT accountancy advice. All
 * data lives in the browser (localStorage), never touches the server.
 * ══════════════════════════════════════════════════════════════════════ */

const STORAGE_KEY = "jcc_tax_confidence_kit_v1";

const GBP = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" });
const gbp = (n) => (Number.isFinite(n) && n !== 0 ? GBP.format(n) : n === 0 ? "£0.00" : "—");
const numOrZero = (v) => {
  const x = Number.parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};

const DISCLAIMER =
  "Just Care Community provides practical admin tools and general guidance only. This is not tax, legal or accountancy advice. Tax rules can change and individual circumstances vary. Always check with HMRC or a qualified accountant before making decisions.";

const EXPENSE_CATEGORIES = [
  "Training / CPD",
  "DBS / Update Service",
  "Insurance",
  "Professional memberships",
  "Travel",
  "Mileage",
  "Parking / tolls / public transport",
  "Phone / admin",
  "Printing / stationery",
  "Equipment or supplies",
  "Uniform / clothing notes",
  "Other",
];

const WORK_TYPES = [
  "Hourly",
  "Live-in",
  "Respite",
  "Retainer",
  "Cancellation",
  "Other",
];

const RECEIPT_CHECKLIST = [
  "Bank statements",
  "Invoices sent",
  "Payment confirmations",
  "Receipts",
  "Mileage log",
  "Training certificates",
  "DBS / Update Service proof",
  "Insurance documents",
  "Professional memberships",
  "Accountant questions",
];

const ACCOUNTANT_QUESTIONS = [
  "Am I recording my income correctly?",
  "Should reimbursements be recorded separately?",
  "Can I claim this travel or mileage?",
  "Are my training / DBS / insurance costs recorded correctly?",
  "What records should I keep, and for how long?",
  "Should I use cash basis or another method?",
  "Do I need to make payments on account?",
  "How much should I set aside for tax and NI?",
  "Am I definitely self-employed for this work?",
];

const GOV_LINKS = [
  { label: "Self Assessment — overview", href: "https://www.gov.uk/self-assessment-tax-returns" },
  { label: "Register as a sole trader", href: "https://www.gov.uk/set-up-sole-trader" },
  { label: "Business records if you're self-employed", href: "https://www.gov.uk/self-employed-records" },
  { label: "Self Assessment deadlines", href: "https://www.gov.uk/self-assessment-tax-returns/deadlines" },
  { label: "Simplified expenses if you're self-employed", href: "https://www.gov.uk/simpler-income-tax-simplified-expenses" },
];

const GETTING_STARTED_PROMPTS = [
  "Are you employed, self-employed, or both?",
  "Have you registered for Self Assessment if it applies to you?",
  "Do you have your UTR (Unique Taxpayer Reference)?",
  "Are you keeping income records for each client or placement?",
  "Are you keeping expense records and receipts?",
  "Have you checked the current Self Assessment deadlines on GOV.UK?",
];

/* ── initial state shape ─────────────────────────────────────────────── */
const emptyIncomeRow = () => ({
  id: crypto.randomUUID(),
  client: "",
  date: "",
  type: WORK_TYPES[0],
  hours: "",
  days: "",
  rate: "",
  invoiced: "",
  paid: "",
  paymentDate: "",
  notes: "",
});
const emptyExpenseRow = () => ({
  id: crypto.randomUUID(),
  date: "",
  category: EXPENSE_CATEGORIES[0],
  description: "",
  amount: "",
  receiptKept: "No",
  notes: "",
});
const emptyMileageRow = () => ({
  id: crypto.randomUUID(),
  date: "",
  fromTo: "",
  purpose: "",
  miles: "",
  extraCosts: "",
  reimbursed: "No",
  notes: "",
});
const emptyInvoiceRow = () => ({
  id: crypto.randomUUID(),
  invoiceNumber: "",
  client: "",
  invoiceDate: "",
  workPeriod: "",
  amount: "",
  sent: "No",
  paid: "No",
  paymentDate: "",
  notes: "",
});
const emptyInvoiceItemRow = () => ({
  id: crypto.randomUUID(),
  description: "",
  qty: "",
  rate: "",
});

const DEFAULTS = () => ({
  taxYear: "",
  gettingStarted: {},
  income: [emptyIncomeRow()],
  expenses: [emptyExpenseRow()],
  mileage: [emptyMileageRow()],
  invoices: [emptyInvoiceRow()],
  receipts: {},
  receiptsOrganised: "Partly",
  accountantNotes: "",
  accountantMainQuestions: "",
  invoiceDraft: {
    businessName: "",
    businessContact: "",
    invoiceNumber: "",
    client: "",
    invoiceDate: "",
    workPeriod: "",
    notes: "",
    items: [emptyInvoiceItemRow()],
  },
});

/* ── tiny UI helpers ─────────────────────────────────────────────────── */
function Section({ id, eyebrow, title, icon: Icon, children, testid }) {
  return (
    <section id={id} data-testid={testid} className="scroll-mt-24">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
        <div className="flex items-start gap-4 mb-5">
          {Icon && (
            <div className="w-11 h-11 rounded-xl bg-purple/10 text-purple flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5" strokeWidth={1.75} />
            </div>
          )}
          <div className="flex-1">
            {eyebrow && <div className="overline text-purple">{eyebrow}</div>}
            <h2 className="font-display font-semibold text-xl md:text-2xl mt-1">{title}</h2>
          </div>
        </div>
        <div className="space-y-4 text-sm text-ink leading-relaxed">
          {children}
        </div>
      </div>
    </section>
  );
}

function DisclaimerBox({ testid }) {
  return (
    <div
      className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5 flex gap-3"
      data-testid={testid}
    >
      <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" strokeWidth={1.75} />
      <p className="text-sm text-ink-muted leading-relaxed">{DISCLAIMER}</p>
    </div>
  );
}

function InlineInput({ value, onChange, placeholder, type = "text", testid, step, min }) {
  return (
    <input
      type={type}
      value={value}
      step={step}
      min={min}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple/30"
    />
  );
}

function InlineSelect({ value, onChange, options, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple/30"
    >
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

function DeleteBtn({ onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="p-1.5 rounded-md text-rose-600 hover:bg-rose-50"
      aria-label="Remove row"
    >
      <Trash2 className="w-4 h-4" strokeWidth={1.75} />
    </button>
  );
}

function AddRowBtn({ onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="btn-outline text-xs mt-3"
      data-testid={testid}
    >
      <Plus className="w-3.5 h-3.5" strokeWidth={1.75} /> {label}
    </button>
  );
}

/* ── CSV helpers ─────────────────────────────────────────────────────── */
function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
function csvBlob(rows) {
  return new Blob([rows.map((r) => r.map(csvEscape).join(",")).join("\n")], {
    type: "text/csv;charset=utf-8",
  });
}
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ══════════════════════════════════════════════════════════════════════
 * Main component
 * ══════════════════════════════════════════════════════════════════════ */
export default function TaxConfidenceKit() {
  const [s, setS] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Merge with defaults so newly added fields don't break older saves.
        return { ...DEFAULTS(), ...parsed };
      }
    } catch (_) { /* ignore */ }
    return DEFAULTS();
  });

  // Persist to localStorage on every change. Debounced by React's own render.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (_) { /* storage full / disabled */ }
  }, [s]);

  const [copied, setCopied] = useState(false);
  const printRef = useRef(null);

  // ── field setters ─────────────────────────────────────────────────
  const setField = (k) => (v) => setS((prev) => ({ ...prev, [k]: v }));
  const setRow = (section, id, key) => (v) =>
    setS((prev) => ({
      ...prev,
      [section]: prev[section].map((row) => (row.id === id ? { ...row, [key]: v } : row)),
    }));
  const addRow = (section, makeRow) => () =>
    setS((prev) => ({ ...prev, [section]: [...prev[section], makeRow()] }));
  const removeRow = (section, id) => () =>
    setS((prev) => ({ ...prev, [section]: prev[section].filter((r) => r.id !== id) }));

  // ── invoice generator draft setters ────────────────────────────────
  const setDraftField = (k) => (v) =>
    setS((prev) => ({ ...prev, invoiceDraft: { ...prev.invoiceDraft, [k]: v } }));
  const setDraftItem = (id, key) => (v) =>
    setS((prev) => ({
      ...prev,
      invoiceDraft: {
        ...prev.invoiceDraft,
        items: prev.invoiceDraft.items.map((it) => (it.id === id ? { ...it, [key]: v } : it)),
      },
    }));
  const addDraftItem = () =>
    setS((prev) => ({
      ...prev,
      invoiceDraft: { ...prev.invoiceDraft, items: [...prev.invoiceDraft.items, emptyInvoiceItemRow()] },
    }));
  const removeDraftItem = (id) =>
    setS((prev) => ({
      ...prev,
      invoiceDraft: { ...prev.invoiceDraft, items: prev.invoiceDraft.items.filter((it) => it.id !== id) },
    }));
  const clearInvoiceDraft = () => {
    if (window.confirm("Clear the invoice generator form? This won't affect your saved Invoice Record.")) {
      setS((prev) => ({ ...prev, invoiceDraft: DEFAULTS().invoiceDraft }));
    }
  };

  const resetAll = () => {
    if (window.confirm("Reset all Tax Confidence Kit data on this device? This cannot be undone.")) {
      setS(DEFAULTS());
    }
  };

  // ── totals (memoised) ─────────────────────────────────────────────
  const totals = useMemo(() => {
    const incomeInvoiced = s.income.reduce((a, r) => a + numOrZero(r.invoiced), 0);
    const incomePaid = s.income.reduce((a, r) => a + numOrZero(r.paid), 0);
    const expensesTotal = s.expenses.reduce((a, r) => a + numOrZero(r.amount), 0);
    const milesTotal = s.mileage.reduce((a, r) => a + numOrZero(r.miles), 0);
    const mileageExtraTotal = s.mileage.reduce((a, r) => a + numOrZero(r.extraCosts), 0);
    const invoicesTotal = s.invoices.reduce((a, r) => a + numOrZero(r.amount), 0);
    const invoicesUnpaid = s.invoices.filter(
      (r) => r.paid !== "Yes" && (r.invoiceNumber || r.client || r.amount)
    ).length;
    return {
      incomeInvoiced,
      incomePaid,
      expensesTotal,
      milesTotal,
      mileageExtraTotal,
      invoicesTotal,
      invoicesCount: s.invoices.filter((r) => r.invoiceNumber || r.client || r.amount).length,
      invoicesUnpaid,
    };
  }, [s]);

  // ── invoice generator draft totals (memoised) ──────────────────────
  const draftTotals = useMemo(() => {
    const items = s.invoiceDraft.items.map((it) => ({
      ...it,
      lineTotal: numOrZero(it.qty) * numOrZero(it.rate),
    }));
    const subtotal = items.reduce((a, it) => a + it.lineTotal, 0);
    return { items, subtotal, total: subtotal };
  }, [s.invoiceDraft]);

  const [savedToRecords, setSavedToRecords] = useState(false);

  const onPrintInvoice = () => {
    const d = s.invoiceDraft;
    const rowsHtml = draftTotals.items
      .filter((it) => it.description || it.qty || it.rate)
      .map(
        (it) => `<tr>
          <td style="padding:8px;border-bottom:1px solid #e5e7eb;">${it.description || ""}</td>
          <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right;">${it.qty || ""}</td>
          <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right;">${it.rate ? gbp(numOrZero(it.rate)) : ""}</td>
          <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right;">${gbp(it.lineTotal)}</td>
        </tr>`
      )
      .join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" />
      <title>Invoice ${d.invoiceNumber || ""}</title>
      <style>
        body { font-family: 'Work Sans', Arial, sans-serif; color: #1f2430; margin: 0; padding: 40px; }
        .accent-bar { height: 6px; background: linear-gradient(90deg,#0E9BA4,#6A1B9A); border-radius: 6px; margin-bottom: 28px; }
        h1 { font-size: 22px; margin: 0 0 4px; }
        .muted { color: #6b7280; font-size: 13px; }
        .grid2 { display: flex; justify-content: space-between; margin: 24px 0; gap: 24px; }
        .box h3 { font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #6b7280; margin: 0 0 6px; }
        .box p { margin: 2px 0; font-size: 14px; }
        table { width: 100%; border-collapse: collapse; margin-top: 16px; }
        th { text-align: left; font-size: 12px; text-transform: uppercase; color: #6b7280; padding: 8px; border-bottom: 2px solid #1f2430; }
        th:nth-child(2), th:nth-child(3), th:nth-child(4) { text-align: right; }
        .totals { margin-top: 16px; margin-left: auto; width: 260px; }
        .totals div { display: flex; justify-content: space-between; padding: 6px 8px; font-size: 14px; }
        .totals .total { font-weight: 700; font-size: 16px; border-top: 2px solid #1f2430; margin-top: 4px; }
        .notes { margin-top: 28px; font-size: 13px; color: #374151; white-space: pre-wrap; }
        .footer { margin-top: 40px; font-size: 11px; color: #9ca3af; }
      </style></head><body>
      <div class="accent-bar"></div>
      <h1>Invoice ${d.invoiceNumber || ""}</h1>
      <div class="muted">Date: ${d.invoiceDate || "—"}${d.workPeriod ? " &middot; Work period: " + d.workPeriod : ""}</div>
      <div class="grid2">
        <div class="box">
          <h3>From</h3>
          <p>${d.businessName || ""}</p>
          <p class="muted">${d.businessContact || ""}</p>
        </div>
        <div class="box">
          <h3>Bill to</h3>
          <p>${d.client || ""}</p>
        </div>
      </div>
      <table>
        <thead><tr><th>Description</th><th>Qty / hours</th><th>Rate</th><th>Amount</th></tr></thead>
        <tbody>${rowsHtml || `<tr><td colspan="4" style="padding:8px;color:#9ca3af;">No line items added</td></tr>`}</tbody>
      </table>
      <div class="totals">
        <div class="total"><span>Total due</span><span>${gbp(draftTotals.total)}</span></div>
      </div>
      ${d.notes ? `<div class="notes"><strong>Notes:</strong>\n${d.notes}</div>` : ""}
      <div class="footer">Generated with the Just Care Community Tax Confidence Kit. This invoice is not tax, legal or accountancy advice.</div>
      </body></html>`;
    const win = window.open("", "_blank", "width=850,height=1000");
    if (!win) return;
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };

  const onSaveInvoiceToRecords = () => {
    const d = s.invoiceDraft;
    if (!d.client && !d.invoiceNumber && draftTotals.total === 0) {
      window.alert("Add at least a client name or an amount before saving to your Invoice Record.");
      return;
    }
    const newRow = {
      id: crypto.randomUUID(),
      invoiceNumber: d.invoiceNumber || "",
      client: d.client || "",
      invoiceDate: d.invoiceDate || "",
      workPeriod: d.workPeriod || "",
      amount: draftTotals.total ? draftTotals.total.toFixed(2) : "",
      sent: "No",
      paid: "No",
      paymentDate: "",
      notes: d.notes || "",
    };
    setS((prev) => ({ ...prev, invoices: [...prev.invoices, newRow] }));
    setSavedToRecords(true);
    setTimeout(() => setSavedToRecords(false), 2500);
  };


  const accountantSummary = useMemo(() => {
    const lines = [
      "JCC Tax Confidence Kit — Accountant Handover Sheet",
      `Tax year: ${s.taxYear || "—"}`,
      "",
      `Total income invoiced: ${gbp(totals.incomeInvoiced)}`,
      `Total income paid:      ${gbp(totals.incomePaid)}`,
      `Total expenses:         ${gbp(totals.expensesTotal)}`,
      `Total mileage:          ${totals.milesTotal.toFixed(1)} miles`,
      `Mileage-related costs:  ${gbp(totals.mileageExtraTotal)}`,
      `Invoices recorded:      ${totals.invoicesCount}`,
      `Invoices not yet paid:  ${totals.invoicesUnpaid}`,
      `Receipts organised:     ${s.receiptsOrganised}`,
      "",
      "Main questions for accountant:",
      (s.accountantMainQuestions || "(none entered)").trim(),
      "",
      "Notes:",
      (s.accountantNotes || "(none entered)").trim(),
      "",
      "-------------------------------------------------------------",
      "Just Care Community provides practical admin tools and general",
      "guidance only. This is not tax, legal or accountancy advice.",
      "Tax rules can change and individual circumstances vary. Always",
      "check with HMRC or a qualified accountant before making decisions.",
    ];
    return lines.join("\n");
  }, [s, totals]);

  // ── actions: print, copy, csv, reset ─────────────────────────────
  const onPrint = () => window.print();
  const onCopyAccountant = async () => {
    try {
      await navigator.clipboard.writeText(accountantSummary);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* clipboard unavailable */ }
  };
  const onDownloadAccountantCSV = () => {
    const rows = [
      ["JCC Tax Confidence Kit — Accountant Handover"],
      ["Tax year", s.taxYear || ""],
      [""],
      ["Total income invoiced (GBP)", totals.incomeInvoiced.toFixed(2)],
      ["Total income paid (GBP)", totals.incomePaid.toFixed(2)],
      ["Total expenses (GBP)", totals.expensesTotal.toFixed(2)],
      ["Total mileage (miles)", totals.milesTotal.toFixed(1)],
      ["Mileage-related costs (GBP)", totals.mileageExtraTotal.toFixed(2)],
      ["Invoices recorded", totals.invoicesCount],
      ["Invoices not yet paid", totals.invoicesUnpaid],
      ["Receipts organised", s.receiptsOrganised],
      [""],
      ["Main questions for accountant"],
      [s.accountantMainQuestions || ""],
      [""],
      ["Notes"],
      [s.accountantNotes || ""],
    ];
    downloadBlob(csvBlob(rows), `tax-confidence-kit-accountant-${s.taxYear || "summary"}.csv`);
  };
  const onDownloadFullCSV = () => {
    const rows = [];
    rows.push(["INCOME"]);
    rows.push(["Client", "Date", "Type", "Hours", "Days", "Rate", "Invoiced", "Paid", "Payment date", "Notes"]);
    s.income.forEach((r) => rows.push([r.client, r.date, r.type, r.hours, r.days, r.rate, r.invoiced, r.paid, r.paymentDate, r.notes]));
    rows.push([""]);
    rows.push(["EXPENSES"]);
    rows.push(["Date", "Category", "Description", "Amount", "Receipt kept", "Notes"]);
    s.expenses.forEach((r) => rows.push([r.date, r.category, r.description, r.amount, r.receiptKept, r.notes]));
    rows.push([""]);
    rows.push(["MILEAGE & TRAVEL"]);
    rows.push(["Date", "From/To", "Purpose", "Miles", "Parking/tolls/fares (GBP)", "Reimbursed", "Notes"]);
    s.mileage.forEach((r) => rows.push([r.date, r.fromTo, r.purpose, r.miles, r.extraCosts, r.reimbursed, r.notes]));
    rows.push([""]);
    rows.push(["INVOICES"]);
    rows.push(["Invoice #", "Client", "Invoice date", "Work period", "Amount", "Sent", "Paid", "Payment date", "Notes"]);
    s.invoices.forEach((r) => rows.push([r.invoiceNumber, r.client, r.invoiceDate, r.workPeriod, r.amount, r.sent, r.paid, r.paymentDate, r.notes]));
    downloadBlob(csvBlob(rows), `tax-confidence-kit-records-${s.taxYear || "all"}.csv`);
  };

  /* ── render ────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-surface-carer" data-testid="page-tax-confidence-kit">
      <div className="container-jcc py-8 md:py-14 max-w-6xl">
        <Link
          to="/workspace/pro-carer-circle-hub"
          className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink"
          data-testid="tck-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Pro Carer Office hub
        </Link>

        <header className="mt-6">
          <div className="overline text-purple">Tax & Admin Support</div>
          <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl">Tax Confidence Kit</h1>
          <p className="mt-3 text-ink-muted max-w-3xl leading-relaxed">
            Practical admin support for independent carers who want to stay organised,
            track clearly, and feel more prepared when tax season comes around.
            Your figures stay on this device.
          </p>
        </header>

        {/* Top disclaimer */}
        <div className="mt-6">
          <DisclaimerBox testid="tck-disclaimer-top" />
        </div>

        {/* Sticky action bar */}
        <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-4 flex flex-wrap gap-2 print:hidden" data-testid="tck-actions">
          <button type="button" onClick={onPrint} className="btn-outline text-sm" data-testid="tck-print">
            <Printer className="w-4 h-4" strokeWidth={1.75} /> Print / save
          </button>
          <button type="button" onClick={onCopyAccountant} className="btn-outline text-sm" data-testid="tck-copy-accountant">
            <ClipboardCopy className="w-4 h-4" strokeWidth={1.75} /> {copied ? "Copied" : "Copy accountant summary"}
          </button>
          <button type="button" onClick={onDownloadAccountantCSV} className="btn-outline text-sm" data-testid="tck-download-accountant-csv">
            <Download className="w-4 h-4" strokeWidth={1.75} /> Download accountant summary (CSV)
          </button>
          <button type="button" onClick={onDownloadFullCSV} className="btn-outline text-sm" data-testid="tck-download-full-csv">
            <Download className="w-4 h-4" strokeWidth={1.75} /> Download all records (CSV)
          </button>
          <button type="button" onClick={resetAll} className="btn-outline text-sm !border-rose-200 !text-rose-700 hover:!bg-rose-50" data-testid="tck-reset">
            <RotateCcw className="w-4 h-4" strokeWidth={1.75} /> Reset all
          </button>
        </div>

        <div ref={printRef} className="mt-8 space-y-6">
          {/* Overview */}
          <Section
            id="overview"
            eyebrow="Overview"
            title="What this kit helps you compile"
            icon={ClipboardList}
            testid="tck-section-overview"
          >
            <p className="text-ink-muted">
              A kit for organising the paperwork you&rsquo;ll need for Self
              Assessment or for a conversation with an accountant. It brings
              together in one place:
            </p>
            <ul className="list-disc pl-5 text-ink-muted space-y-1.5">
              <li>Income records — who paid you, when, how much</li>
              <li>Expense records — business costs you&rsquo;ve tracked</li>
              <li>Mileage and travel records</li>
              <li>Invoice records — sent, paid, outstanding</li>
              <li>A receipts checklist to keep evidence in one place</li>
              <li>Questions to ask an accountant</li>
              <li>A year-end summary you can print or copy</li>
            </ul>

            <label className="block mt-4 max-w-sm">
              <span className="text-sm font-medium text-ink">Tax year (e.g. 2025/26)</span>
              <input
                type="text"
                value={s.taxYear}
                onChange={(e) => setField("taxYear")(e.target.value)}
                placeholder="2025/26"
                data-testid="tck-tax-year"
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple/30"
              />
            </label>
          </Section>

          {/* Getting Started */}
          <Section
            id="getting-started"
            eyebrow="Getting Started"
            title="A few plain-English prompts to check first"
            icon={HelpCircle}
            testid="tck-section-getting-started"
          >
            <p className="text-ink-muted">
              These prompts help you sanity-check your setup. Tick anything you&rsquo;ve
              already done. Anything you&rsquo;re unsure about is worth checking on
              GOV.UK or with an accountant before your Self Assessment.
            </p>
            <ul className="space-y-2.5">
              {GETTING_STARTED_PROMPTS.map((q, idx) => (
                <li key={q} className="flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    checked={!!s.gettingStarted[q]}
                    onChange={(e) =>
                      setS((prev) => ({
                        ...prev,
                        gettingStarted: { ...prev.gettingStarted, [q]: e.target.checked },
                      }))
                    }
                    data-testid={`tck-getting-started-${idx}`}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-purple focus:ring-purple/30"
                  />
                  <span className="text-ink">{q}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3 flex gap-2 text-xs text-ink-muted">
              <Info className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.75} />
              <span>
                For definitive guidance on what applies to you, use GOV.UK or a qualified
                accountant. Links to the most useful GOV.UK pages are at the bottom of
                this kit.
              </span>
            </div>
          </Section>

          {/* Income tracker */}
          <Section
            id="income"
            eyebrow="Income Tracker"
            title="Record what you invoice and what you&rsquo;re paid"
            icon={Receipt}
            testid="tck-section-income"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-ink-muted border-b border-slate-200">
                    <th className="text-left p-2">Client / placement</th>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Type</th>
                    <th className="text-left p-2">Hours</th>
                    <th className="text-left p-2">Days</th>
                    <th className="text-left p-2">Rate</th>
                    <th className="text-left p-2">Invoiced</th>
                    <th className="text-left p-2">Paid</th>
                    <th className="text-left p-2">Payment date</th>
                    <th className="text-left p-2">Notes</th>
                    <th className="p-2 print:hidden"></th>
                  </tr>
                </thead>
                <tbody data-testid="tck-income-tbody">
                  {s.income.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 align-top" data-testid={`tck-income-row-${r.id}`}>
                      <td className="p-1.5"><InlineInput value={r.client} onChange={setRow("income", r.id, "client")} placeholder="Initials" testid={`tck-income-client-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput type="date" value={r.date} onChange={setRow("income", r.id, "date")} testid={`tck-income-date-${r.id}`} /></td>
                      <td className="p-1.5"><InlineSelect value={r.type} onChange={setRow("income", r.id, "type")} options={WORK_TYPES} testid={`tck-income-type-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput type="number" step="0.25" min="0" value={r.hours ?? ""} onChange={setRow("income", r.id, "hours")} placeholder="0" testid={`tck-income-hours-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput type="number" step="0.25" min="0" value={r.days ?? ""} onChange={setRow("income", r.id, "days")} placeholder="0" testid={`tck-income-days-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput type="number" step="0.01" min="0" value={r.rate} onChange={setRow("income", r.id, "rate")} placeholder="£" testid={`tck-income-rate-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput type="number" step="0.01" min="0" value={r.invoiced} onChange={setRow("income", r.id, "invoiced")} placeholder="£" testid={`tck-income-invoiced-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput type="number" step="0.01" min="0" value={r.paid} onChange={setRow("income", r.id, "paid")} placeholder="£" testid={`tck-income-paid-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput type="date" value={r.paymentDate} onChange={setRow("income", r.id, "paymentDate")} testid={`tck-income-paymentDate-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput value={r.notes} onChange={setRow("income", r.id, "notes")} placeholder="Notes" testid={`tck-income-notes-${r.id}`} /></td>
                      <td className="p-1.5 print:hidden"><DeleteBtn onClick={removeRow("income", r.id)} testid={`tck-income-remove-${r.id}`} /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="text-xs font-semibold text-ink bg-slate-50">
                    <td className="p-2" colSpan={6}>Totals</td>
                    <td className="p-2" data-testid="tck-income-total-invoiced">{gbp(totals.incomeInvoiced)}</td>
                    <td className="p-2" data-testid="tck-income-total-paid">{gbp(totals.incomePaid)}</td>
                    <td className="p-2" colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <AddRowBtn onClick={addRow("income", emptyIncomeRow)} label="Add income row" testid="tck-income-add" />
          </Section>

          {/* Expense tracker */}
          <Section
            id="expenses"
            eyebrow="Expense Tracker"
            title="Track business costs so nothing gets missed"
            icon={FileText}
            testid="tck-section-expenses"
          >
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 flex gap-2 text-xs text-ink-muted">
              <Info className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.75} />
              <span>
                Not every cost is automatically allowable. Use this tracker to organise
                your records and ask your accountant or HMRC what applies to you.
              </span>
            </div>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-ink-muted border-b border-slate-200">
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Category</th>
                    <th className="text-left p-2">Description</th>
                    <th className="text-left p-2">Amount</th>
                    <th className="text-left p-2">Receipt kept?</th>
                    <th className="text-left p-2">Notes / ask accountant</th>
                    <th className="p-2 print:hidden"></th>
                  </tr>
                </thead>
                <tbody data-testid="tck-expenses-tbody">
                  {s.expenses.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 align-top" data-testid={`tck-expenses-row-${r.id}`}>
                      <td className="p-1.5"><InlineInput type="date" value={r.date} onChange={setRow("expenses", r.id, "date")} testid={`tck-expenses-date-${r.id}`} /></td>
                      <td className="p-1.5"><InlineSelect value={r.category} onChange={setRow("expenses", r.id, "category")} options={EXPENSE_CATEGORIES} testid={`tck-expenses-category-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput value={r.description} onChange={setRow("expenses", r.id, "description")} placeholder="What was it for?" testid={`tck-expenses-description-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput type="number" step="0.01" min="0" value={r.amount} onChange={setRow("expenses", r.id, "amount")} placeholder="£" testid={`tck-expenses-amount-${r.id}`} /></td>
                      <td className="p-1.5"><InlineSelect value={r.receiptKept} onChange={setRow("expenses", r.id, "receiptKept")} options={["Yes", "No"]} testid={`tck-expenses-receipt-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput value={r.notes} onChange={setRow("expenses", r.id, "notes")} placeholder="Notes" testid={`tck-expenses-notes-${r.id}`} /></td>
                      <td className="p-1.5 print:hidden"><DeleteBtn onClick={removeRow("expenses", r.id)} testid={`tck-expenses-remove-${r.id}`} /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="text-xs font-semibold text-ink bg-slate-50">
                    <td className="p-2" colSpan={3}>Total expenses</td>
                    <td className="p-2" data-testid="tck-expenses-total">{gbp(totals.expensesTotal)}</td>
                    <td className="p-2" colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <AddRowBtn onClick={addRow("expenses", emptyExpenseRow)} label="Add expense row" testid="tck-expenses-add" />
          </Section>

          {/* Mileage & travel */}
          <Section
            id="mileage"
            eyebrow="Mileage & Travel Log"
            title="Keep clear journey records"
            icon={Car}
            testid="tck-section-mileage"
          >
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 flex gap-2 text-xs text-ink-muted">
              <Info className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.75} />
              <span>
                Keep clear journey records and check the correct treatment with HMRC
                or your accountant. This log is for organisation only.
              </span>
            </div>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-ink-muted border-b border-slate-200">
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">From / to</th>
                    <th className="text-left p-2">Purpose</th>
                    <th className="text-left p-2">Miles</th>
                    <th className="text-left p-2">Parking / tolls / fares (£)</th>
                    <th className="text-left p-2">Reimbursed?</th>
                    <th className="text-left p-2">Notes</th>
                    <th className="p-2 print:hidden"></th>
                  </tr>
                </thead>
                <tbody data-testid="tck-mileage-tbody">
                  {s.mileage.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 align-top" data-testid={`tck-mileage-row-${r.id}`}>
                      <td className="p-1.5"><InlineInput type="date" value={r.date} onChange={setRow("mileage", r.id, "date")} testid={`tck-mileage-date-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput value={r.fromTo} onChange={setRow("mileage", r.id, "fromTo")} placeholder="Home → client" testid={`tck-mileage-fromTo-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput value={r.purpose} onChange={setRow("mileage", r.id, "purpose")} placeholder="e.g. shift" testid={`tck-mileage-purpose-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput type="number" step="0.1" min="0" value={r.miles} onChange={setRow("mileage", r.id, "miles")} placeholder="0" testid={`tck-mileage-miles-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput type="number" step="0.01" min="0" value={r.extraCosts} onChange={setRow("mileage", r.id, "extraCosts")} placeholder="£" testid={`tck-mileage-extraCosts-${r.id}`} /></td>
                      <td className="p-1.5"><InlineSelect value={r.reimbursed} onChange={setRow("mileage", r.id, "reimbursed")} options={["Yes", "No"]} testid={`tck-mileage-reimbursed-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput value={r.notes} onChange={setRow("mileage", r.id, "notes")} placeholder="Notes" testid={`tck-mileage-notes-${r.id}`} /></td>
                      <td className="p-1.5 print:hidden"><DeleteBtn onClick={removeRow("mileage", r.id)} testid={`tck-mileage-remove-${r.id}`} /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="text-xs font-semibold text-ink bg-slate-50">
                    <td className="p-2" colSpan={3}>Totals</td>
                    <td className="p-2" data-testid="tck-mileage-total-miles">{totals.milesTotal.toFixed(1)} miles</td>
                    <td className="p-2" data-testid="tck-mileage-total-extra">{gbp(totals.mileageExtraTotal)}</td>
                    <td className="p-2" colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <AddRowBtn onClick={addRow("mileage", emptyMileageRow)} label="Add mileage row" testid="tck-mileage-add" />
          </Section>

          {/* Invoice Generator */}
          <Section
            id="invoice-generator"
            eyebrow="Invoice Generator"
            title="Create a simple invoice, then save it to your Invoice Record"
            icon={FilePlus2}
            testid="tck-section-invoice-generator"
          >
            <p className="text-ink-muted">
              Fill in the details below — the total is calculated automatically. Print
              it as a clean, standalone invoice (use your browser&rsquo;s &ldquo;Save
              as PDF&rdquo; option), then save the summary straight into your Invoice
              Record below.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm font-medium text-ink">Your name / business name</span>
                <input
                  type="text"
                  value={s.invoiceDraft.businessName}
                  onChange={(e) => setDraftField("businessName")(e.target.value)}
                  placeholder="e.g. Jane Smith Independent Care"
                  data-testid="tck-invgen-business-name"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple/30"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-ink">Contact details (email / phone)</span>
                <input
                  type="text"
                  value={s.invoiceDraft.businessContact}
                  onChange={(e) => setDraftField("businessContact")(e.target.value)}
                  placeholder="jane@example.com · 07…"
                  data-testid="tck-invgen-business-contact"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple/30"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-ink">Invoice number</span>
                <input
                  type="text"
                  value={s.invoiceDraft.invoiceNumber}
                  onChange={(e) => setDraftField("invoiceNumber")(e.target.value)}
                  placeholder="e.g. INV-0012"
                  data-testid="tck-invgen-invoice-number"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple/30"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-ink">Client</span>
                <input
                  type="text"
                  value={s.invoiceDraft.client}
                  onChange={(e) => setDraftField("client")(e.target.value)}
                  placeholder="Client or family name"
                  data-testid="tck-invgen-client"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple/30"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-ink">Invoice date</span>
                <input
                  type="date"
                  value={s.invoiceDraft.invoiceDate}
                  onChange={(e) => setDraftField("invoiceDate")(e.target.value)}
                  data-testid="tck-invgen-invoice-date"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple/30"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-ink">Work period</span>
                <input
                  type="text"
                  value={s.invoiceDraft.workPeriod}
                  onChange={(e) => setDraftField("workPeriod")(e.target.value)}
                  placeholder="e.g. 1–14 June 2026"
                  data-testid="tck-invgen-work-period"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple/30"
                />
              </label>
            </div>

            <div className="overflow-x-auto mt-4">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-ink-muted border-b border-slate-200">
                    <th className="text-left p-2">Description</th>
                    <th className="text-left p-2">Qty / hours</th>
                    <th className="text-left p-2">Rate</th>
                    <th className="text-left p-2">Amount</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody data-testid="tck-invgen-items-tbody">
                  {draftTotals.items.map((it) => (
                    <tr key={it.id} className="border-b border-slate-100 align-top" data-testid={`tck-invgen-item-row-${it.id}`}>
                      <td className="p-1.5">
                        <InlineInput value={it.description} onChange={setDraftItem(it.id, "description")} placeholder="e.g. Weekday care hours" testid={`tck-invgen-item-description-${it.id}`} />
                      </td>
                      <td className="p-1.5">
                        <InlineInput type="number" step="0.25" min="0" value={it.qty} onChange={setDraftItem(it.id, "qty")} placeholder="0" testid={`tck-invgen-item-qty-${it.id}`} />
                      </td>
                      <td className="p-1.5">
                        <InlineInput type="number" step="0.01" min="0" value={it.rate} onChange={setDraftItem(it.id, "rate")} placeholder="£" testid={`tck-invgen-item-rate-${it.id}`} />
                      </td>
                      <td className="p-1.5 text-xs font-medium" data-testid={`tck-invgen-item-amount-${it.id}`}>{gbp(it.lineTotal)}</td>
                      <td className="p-1.5"><DeleteBtn onClick={() => removeDraftItem(it.id)} testid={`tck-invgen-item-remove-${it.id}`} /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="text-xs font-semibold text-ink bg-slate-50">
                    <td className="p-2" colSpan={3}>Total due</td>
                    <td className="p-2" data-testid="tck-invgen-total">{gbp(draftTotals.total)}</td>
                    <td className="p-2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <AddRowBtn onClick={addDraftItem} label="Add line item" testid="tck-invgen-item-add" />

            <label className="block mt-4">
              <span className="text-sm font-medium text-ink">Notes / payment terms</span>
              <textarea
                value={s.invoiceDraft.notes}
                onChange={(e) => setDraftField("notes")(e.target.value)}
                rows={3}
                placeholder="e.g. Payment due within 14 days by bank transfer…"
                data-testid="tck-invgen-notes"
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple/30"
              />
            </label>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <button type="button" onClick={onPrintInvoice} className="btn-outline text-sm" data-testid="tck-invgen-print">
                <Printer className="w-4 h-4" strokeWidth={1.75} /> Print invoice
              </button>
              <button type="button" onClick={onSaveInvoiceToRecords} className="btn-purple text-sm" data-testid="tck-invgen-save">
                <Save className="w-4 h-4" strokeWidth={1.75} /> Save to Records
              </button>
              <button type="button" onClick={clearInvoiceDraft} className="btn-outline text-sm" data-testid="tck-invgen-clear">
                <RotateCcw className="w-4 h-4" strokeWidth={1.75} /> Clear form
              </button>
              {savedToRecords && (
                <span className="text-sm text-teal font-medium flex items-center gap-1.5" data-testid="tck-invgen-saved-confirmation">
                  <CheckCircle2 className="w-4 h-4" strokeWidth={1.75} /> Saved to your Invoice Record below
                </span>
              )}
            </div>
          </Section>

          {/* Invoice record */}
          <Section
            id="invoices"
            eyebrow="Invoice Record"
            title="Track invoices sent, paid and outstanding"
            icon={FileText}
            testid="tck-section-invoices"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-ink-muted border-b border-slate-200">
                    <th className="text-left p-2">Invoice #</th>
                    <th className="text-left p-2">Client</th>
                    <th className="text-left p-2">Invoice date</th>
                    <th className="text-left p-2">Work period</th>
                    <th className="text-left p-2">Amount</th>
                    <th className="text-left p-2">Sent?</th>
                    <th className="text-left p-2">Paid?</th>
                    <th className="text-left p-2">Payment date</th>
                    <th className="text-left p-2">Notes</th>
                    <th className="p-2 print:hidden"></th>
                  </tr>
                </thead>
                <tbody data-testid="tck-invoices-tbody">
                  {s.invoices.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 align-top" data-testid={`tck-invoices-row-${r.id}`}>
                      <td className="p-1.5"><InlineInput value={r.invoiceNumber} onChange={setRow("invoices", r.id, "invoiceNumber")} placeholder="#" testid={`tck-invoices-number-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput value={r.client} onChange={setRow("invoices", r.id, "client")} placeholder="Client" testid={`tck-invoices-client-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput type="date" value={r.invoiceDate} onChange={setRow("invoices", r.id, "invoiceDate")} testid={`tck-invoices-invoiceDate-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput value={r.workPeriod} onChange={setRow("invoices", r.id, "workPeriod")} placeholder="e.g. Jun 1–14" testid={`tck-invoices-workPeriod-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput type="number" step="0.01" min="0" value={r.amount} onChange={setRow("invoices", r.id, "amount")} placeholder="£" testid={`tck-invoices-amount-${r.id}`} /></td>
                      <td className="p-1.5"><InlineSelect value={r.sent} onChange={setRow("invoices", r.id, "sent")} options={["Yes", "No"]} testid={`tck-invoices-sent-${r.id}`} /></td>
                      <td className="p-1.5"><InlineSelect value={r.paid} onChange={setRow("invoices", r.id, "paid")} options={["Yes", "No"]} testid={`tck-invoices-paid-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput type="date" value={r.paymentDate} onChange={setRow("invoices", r.id, "paymentDate")} testid={`tck-invoices-paymentDate-${r.id}`} /></td>
                      <td className="p-1.5"><InlineInput value={r.notes} onChange={setRow("invoices", r.id, "notes")} placeholder="Notes" testid={`tck-invoices-notes-${r.id}`} /></td>
                      <td className="p-1.5 print:hidden"><DeleteBtn onClick={removeRow("invoices", r.id)} testid={`tck-invoices-remove-${r.id}`} /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="text-xs font-semibold text-ink bg-slate-50">
                    <td className="p-2" colSpan={4}>Totals</td>
                    <td className="p-2" data-testid="tck-invoices-total">{gbp(totals.invoicesTotal)}</td>
                    <td className="p-2" colSpan={2}></td>
                    <td className="p-2" data-testid="tck-invoices-unpaid" colSpan={2}>Unpaid: {totals.invoicesUnpaid}</td>
                    <td className="p-2 print:hidden"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <AddRowBtn onClick={addRow("invoices", emptyInvoiceRow)} label="Add invoice row" testid="tck-invoices-add" />
          </Section>

          {/* Receipt checklist */}
          <Section
            id="receipts"
            eyebrow="Receipt Checklist"
            title="Have you kept the evidence?"
            icon={CheckCircle2}
            testid="tck-section-receipts"
          >
            <ul className="grid sm:grid-cols-2 gap-2.5">
              {RECEIPT_CHECKLIST.map((item, idx) => (
                <li key={item} className="flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    checked={!!s.receipts[item]}
                    onChange={(e) =>
                      setS((prev) => ({
                        ...prev,
                        receipts: { ...prev.receipts, [item]: e.target.checked },
                      }))
                    }
                    data-testid={`tck-receipt-${idx}`}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-purple focus:ring-purple/30"
                  />
                  <span className="text-ink">{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5">
              <label className="text-sm font-medium text-ink">Overall — how organised are your receipts this year?</label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="tck-receipts-organised-group">
                {["Yes", "Partly", "No"].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setField("receiptsOrganised")(opt)}
                    data-testid={`tck-receipts-organised-${opt.toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-full text-sm border ${
                      s.receiptsOrganised === opt
                        ? "bg-purple text-white border-purple"
                        : "bg-white text-ink border-slate-200 hover:border-purple"
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </Section>

          {/* Accountant handover sheet */}
          <Section
            id="handover"
            eyebrow="Accountant Handover Sheet"
            title="Your one-page summary"
            icon={ClipboardList}
            testid="tck-section-handover"
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50">
                <div className="text-xs uppercase tracking-wide text-ink-muted">Totals from your records</div>
                <dl className="mt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><dt className="text-ink-muted">Tax year</dt><dd className="font-medium">{s.taxYear || "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink-muted">Total income invoiced</dt><dd className="font-medium">{gbp(totals.incomeInvoiced)}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink-muted">Total income paid</dt><dd className="font-medium">{gbp(totals.incomePaid)}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink-muted">Total expenses</dt><dd className="font-medium">{gbp(totals.expensesTotal)}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink-muted">Total mileage</dt><dd className="font-medium">{totals.milesTotal.toFixed(1)} miles</dd></div>
                  <div className="flex justify-between"><dt className="text-ink-muted">Invoices recorded</dt><dd className="font-medium">{totals.invoicesCount}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink-muted">Invoices unpaid</dt><dd className="font-medium">{totals.invoicesUnpaid}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink-muted">Receipts organised</dt><dd className="font-medium">{s.receiptsOrganised}</dd></div>
                </dl>
              </div>
              <div className="space-y-3">
                <label className="block">
                  <span className="text-sm font-medium text-ink">Main questions for accountant</span>
                  <textarea
                    value={s.accountantMainQuestions}
                    onChange={(e) => setField("accountantMainQuestions")(e.target.value)}
                    rows={5}
                    placeholder="One question per line…"
                    data-testid="tck-accountant-questions"
                    className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple/30"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-ink">Notes</span>
                  <textarea
                    value={s.accountantNotes}
                    onChange={(e) => setField("accountantNotes")(e.target.value)}
                    rows={4}
                    placeholder="Anything else your accountant should know…"
                    data-testid="tck-accountant-notes"
                    className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple/30"
                  />
                </label>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2 print:hidden">
              <button type="button" onClick={onCopyAccountant} className="btn-outline text-sm" data-testid="tck-handover-copy">
                <ClipboardCopy className="w-4 h-4" strokeWidth={1.75} /> {copied ? "Copied" : "Copy accountant summary"}
              </button>
              <button type="button" onClick={onPrint} className="btn-outline text-sm" data-testid="tck-handover-print">
                <Printer className="w-4 h-4" strokeWidth={1.75} /> Print accountant handover
              </button>
              <button type="button" onClick={onDownloadAccountantCSV} className="btn-outline text-sm" data-testid="tck-handover-csv">
                <Download className="w-4 h-4" strokeWidth={1.75} /> Download summary CSV
              </button>
            </div>

            <div className="mt-5">
              <div className="text-xs uppercase tracking-wide text-ink-muted mb-2">Preview of the copyable summary</div>
              <pre
                data-testid="tck-handover-preview"
                className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs whitespace-pre-wrap font-mono text-ink-muted max-h-80 overflow-auto"
              >{accountantSummary}</pre>
            </div>
          </Section>

          {/* Questions to ask */}
          <Section
            id="accountant-questions"
            eyebrow="Questions to Ask an Accountant"
            title="Prompts to bring to your first conversation"
            icon={HelpCircle}
            testid="tck-section-questions"
          >
            <ul className="list-disc pl-5 text-ink-muted space-y-1.5">
              {ACCOUNTANT_QUESTIONS.map((q) => (
                <li key={q}>{q}</li>
              ))}
            </ul>
          </Section>

          {/* GOV.UK links */}
          <Section
            id="gov-links"
            eyebrow="Useful GOV.UK links"
            title="For definitive rules and current deadlines"
            icon={BookOpen}
            testid="tck-section-gov-links"
          >
            <ul className="space-y-2.5">
              {GOV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`tck-gov-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="inline-flex items-center gap-1.5 text-purple hover:underline text-sm"
                  >
                    {l.label} <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </a>
                </li>
              ))}
            </ul>
          </Section>

          {/* Bottom disclaimer */}
          <DisclaimerBox testid="tck-disclaimer-bottom" />
        </div>
      </div>
    </div>
  );
}
